import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Grid,
  Typography,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Fab,
  Autocomplete,
  Chip
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSkillSheetMainStepper } from '../context/SkillSheetMainStepperContext';

export default function MiddlewareInfoForm({ isEdit = false }) {
  // コンテキストの取得を条件分岐で行う
  let stepperContext;

  if (isEdit) {
    try {
      stepperContext = useSkillSheetMainStepper();
    } catch (error) {
      console.error('編集用コンテキストの取得に失敗しました:', error);
      throw error;
    }
  } else {
    try {
      stepperContext = useSkillSheetMainStepper();
    } catch (error) {
      console.error('作成用コンテキストの取得に失敗しました:', error);
      throw error;
    }
  }

  const {
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    fetchMasterData,
    fetchMiddlewareProductData,
    masterData,
    validateStep
  } = stepperContext;

  // バリデーション表示の状態
  const [showValidation, setShowValidation] = useState(false);
  // 削除確認ダイアログの状態
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, index: null });
  // 新しく追加された要素へのスクロール用ref
  const formRefs = useRef([]);
  // 各カテゴリーのサービス選択肢を保持
  const [productOptions, setProductOptions] = useState({});
  // 各カテゴリーのサービス入力値を保持
  const [productInputValues, setProductInputValues] = useState({});

  // フォームの値を監視
  const middlewares = watch('middlewares') || [{ middleware_name: '', middleware_id: '', middleware_product_name: '', middleware_product_id: '' }];

  // 既に選択済みのカテゴリーを取得する関数
  const getSelectedMiddlewares = useCallback(() => {
    return middlewares
      .map(middleware => middleware.middleware_name)
      .filter(name => name && name.trim() !== '');
  }, [middlewares]);

  // 既に選択済みのサービスを取得する関数
  const getSelectedProducts = useCallback((currentIndex) => {
    const allProducts = [];
    middlewares.forEach((middleware, index) => {
      if (index !== currentIndex && middleware.middleware_product_name) {
        const products = middleware.middleware_product_name.split(', ').filter(f => f.trim() !== '');
        allProducts.push(...products);
      }
    });
    return allProducts;
  }, [middlewares]);

  // カテゴリーの選択肢をフィルタリングする関数
  const getFilteredMiddlewareOptions = useCallback((currentIndex) => {
    const selectedMiddlewares = getSelectedMiddlewares();
    const currentMiddleware = middlewares[currentIndex]?.middleware_name;

    return (masterData.middlewares || []).filter(middleware => {
      const middlewareName = typeof middleware === 'string' ? middleware : middleware.name;
      // すべてのカテゴリで重複選択を禁止
      return !selectedMiddlewares.includes(middlewareName) || middlewareName === currentMiddleware;
    });
  }, [masterData.middlewares, getSelectedMiddlewares, middlewares]);

  // サービスの選択肢をフィルタリングする関数
  const getFilteredProductOptions = useCallback((currentIndex) => {
    const selectedProducts = getSelectedProducts(currentIndex); // 他のフォームで選択済み
    const currentProducts = middlewares[currentIndex]?.middleware_product_name 
      ? middlewares[currentIndex].middleware_product_name.split(', ').filter(f => f.trim() !== '')
      : []; // 現在のフォームで選択済み

    // 全ての選択済みサービスを結合
    const allSelectedProducts = [...selectedProducts, ...currentProducts];

    return (productOptions[currentIndex] || []).filter(product => {
      const productName = typeof product === 'string' ? product : product.name;
      return !allSelectedProducts.includes(productName);
    });
  }, [productOptions, getSelectedProducts, middlewares]);

  // マスタデータを取得
  useEffect(() => {
    fetchMasterData('middlewares');
  }, [fetchMasterData]);

  // バリデーション関数
  const validateForm = () => {
    let hasError = false;
    clearErrors();

    // middlewaresのバリデーション
    middlewares.forEach((middleware, index) => {
      const hasMiddlewareName = middleware.middleware_name && middleware.middleware_name !== '';
      const hasProductName = middleware.middleware_product_name && middleware.middleware_product_name !== '';

      // どちらか一方のみ入力されている場合はエラー
      if (hasMiddlewareName && !hasProductName) {
        setError(`middlewares.${index}.middleware_product_name`, {
          type: 'required',
          message: 'サービス名は入力必須です'
        });
        hasError = true;
      }

      if (hasProductName && !hasMiddlewareName) {
        setError(`middlewares.${index}.middleware_name`, {
          type: 'required',
          message: 'ミドルウェア名を入力してください'
        });
        hasError = true;
      }
    });

    if (hasError) {
      setShowValidation(true);
      // エラーがある場合、最初のエラーフィールドまでスクロール
      setTimeout(() => {
        const firstErrorField = document.querySelector('[data-error="true"]');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else {
      setShowValidation(false);
    }

    return !hasError;
  };

  // バリデーションエラーが表示された時の処理
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setShowValidation(true);
      // エラーがある場合、最初のエラーフィールドまでスクロール
      setTimeout(() => {
        const firstErrorField = document.querySelector('[data-error="true"]');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else {
      setShowValidation(false);
    }
  }, [errors]);

  // フォームバリデーションを外部から呼び出せるように登録
  useEffect(() => {
    if (validateStep) {
      validateStep(13, validateForm); // 14ステップ目（0ベースで13）
    }
  }, [validateStep, middlewares]);

  // カテゴリー選択の変更ハンドラ
  const handleMiddlewareChange = async (index, selectedMiddleware) => {
    const newMiddlewares = [...middlewares];

    if (typeof selectedMiddleware === 'string') {
      // 手動入力の場合
      newMiddlewares[index] = {
        ...newMiddlewares[index],
        middleware_name: selectedMiddleware,
        middleware_id: '',
        // カテゴリーが変更されたらサービスをクリア
        middleware_product_name: '',
        middleware_product_id: ''
      };
    } else if (selectedMiddleware && selectedMiddleware.name) {
      // オートコンプリートから選択した場合
      newMiddlewares[index] = {
        ...newMiddlewares[index],
        middleware_name: selectedMiddleware.name,
        middleware_id: selectedMiddleware.id || '',
        // カテゴリーが変更されたらサービスをクリア
        middleware_product_name: '',
        middleware_product_id: ''
      };

      // 選択したカテゴリーに基づいてサービスの選択肢を取得
      try {
        const productData = await fetchMiddlewareProductData(selectedMiddleware.name);
        setProductOptions(prev => ({
          ...prev,
          [index]: productData
        }));
      } catch (error) {
        console.error('Error fetching product options:', error);
      }
    } else if (selectedMiddleware === null || selectedMiddleware === '') {
      // クリアされた場合
      newMiddlewares[index] = {
        ...newMiddlewares[index],
        middleware_name: '',
        middleware_id: '',
        middleware_product_name: '',
        middleware_product_id: ''
      };
      // サービスの選択肢もクリア
      setProductOptions(prev => ({
        ...prev,
        [index]: []
      }));
    }

    setValue('middlewares', newMiddlewares, { shouldDirty: true, shouldTouch: true });

    // サービスの入力値もクリア
    setProductInputValues(prev => ({
      ...prev,
      [index]: ''
    }));

    // 変更時に該当フィールドのエラーをクリア
    const fieldErrors = errors?.middlewares;
    if (fieldErrors && fieldErrors[index]) {
      if (fieldErrors[index].middleware_name) {
        clearErrors(`middlewares.${index}.middleware_name`);
      }
      if (fieldErrors[index].middleware_product_name) {
        clearErrors(`middlewares.${index}.middleware_product_name`);
      }
    }
  };

  // サービス選択の変更ハンドラ
  const handleProductChange = (index, newValue) => {
    const newMiddlewares = [...middlewares];

    // 選択されたサービスを文字列に変換
    const productNames = newValue.map(item =>
      typeof item === 'string' ? item : item.name || ''
    ).join(', ');

    newMiddlewares[index] = {
      ...newMiddlewares[index],
      middleware_product_name: productNames
    };
    setValue('middlewares', newMiddlewares, { shouldDirty: true, shouldTouch: true });

    // 変更時に該当フィールドのエラーをクリア
    const fieldErrors = errors?.middlewares;
    if (fieldErrors && fieldErrors[index] && fieldErrors[index].middleware_product_name) {
      clearErrors(`middlewares.${index}.middleware_product_name`);
    }
  };

  // サービス入力値変更ハンドラ
  const handleProductInputChange = (index, newInputValue) => {
    setProductInputValues(prev => ({
      ...prev,
      [index]: newInputValue
    }));
  };

  // サービスの手動入力確定
  const handleProductBlur = (index) => {
    const inputValue = productInputValues[index];
    if (inputValue && inputValue.trim() !== '') {
      const currentProducts = middlewares[index].middleware_product_name
        ? middlewares[index].middleware_product_name.split(', ').filter(f => f.trim() !== '')
        : [];

      const trimmedInput = inputValue.trim();
      if (!currentProducts.includes(trimmedInput)) {
        const newProducts = [...currentProducts, trimmedInput];
        const newMiddlewares = [...middlewares];
        newMiddlewares[index] = {
          ...newMiddlewares[index],
          middleware_product_name: newProducts.join(', ')
        };
        setValue('middlewares', newMiddlewares, { shouldDirty: true, shouldTouch: true });
      }

      // 入力値をクリア
      setProductInputValues(prev => ({
        ...prev,
        [index]: ''
      }));
    }
  };

  // 要素を追加
  const handleAdd = () => {
    const newMiddlewares = [...middlewares, { middleware_name: '', middleware_id: '', middleware_product_name: '', middleware_product_id: '' }];
    setValue('middlewares', newMiddlewares, { shouldDirty: true, shouldTouch: true });

    // 新しい要素にスクロール
    setTimeout(() => {
      const newIndex = newMiddlewares.length - 1;
      if (formRefs.current[newIndex]) {
        formRefs.current[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // 削除前の確認
  const handleDeleteConfirm = (index) => {
    const middleware = middlewares[index];
    const hasContent = (middleware.middleware_name && middleware.middleware_name !== '') ||
                      (middleware.middleware_product_name && middleware.middleware_product_name !== '');

    if (hasContent) {
      setDeleteConfirmDialog({ open: true, index });
    } else {
      handleDelete(index);
    }
  };

  // 要素を削除
  const handleDelete = (index) => {
    const newMiddlewares = middlewares.filter((_, i) => i !== index);
    setValue(
      'middlewares',
      newMiddlewares.length > 0 ? newMiddlewares : [],
      { shouldDirty: true, shouldTouch: true }
    );
    setDeleteConfirmDialog({ open: false, index: null });

    // サービスの選択肢と入力値もクリア
    setProductOptions(prev => {
      const newOptions = { ...prev };
      delete newOptions[index];
      return newOptions;
    });
    setProductInputValues(prev => {
      const newValues = { ...prev };
      delete newValues[index];
      return newValues;
    });

    // 削除に関連するエラーをクリア
    const middlewareNameErrorKey = `middlewares.${index}.middleware_name`;
    const productNameErrorKey = `middlewares.${index}.middleware_product_name`;
    clearErrors(middlewareNameErrorKey);
    clearErrors(productNameErrorKey);
  };

  // バリデーションエラーの取得
  const getFieldError = (index, fieldName) => {
    const fieldErrors = errors?.middlewares;
    if (showValidation && fieldErrors && fieldErrors[index] && fieldErrors[index][fieldName]) {
      return fieldErrors[index][fieldName];
    }
    return null;
  };

  // バリデーションエラーメッセージの生成
  const validationErrors = [];
  if (showValidation && errors?.middlewares) {
    middlewares.forEach((_, index) => {
      const middlewareNameError = getFieldError(index, 'middleware_name');
      const productNameError = getFieldError(index, 'middleware_product_name');
      if (middlewareNameError) validationErrors.push(middlewareNameError.message);
      if (productNameError) validationErrors.push(productNameError.message);
    });
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ marginBottom: '24px' }}>
        MW
      </Typography>

      {/* バリデーションエラーメッセージの表示 */}
      {showValidation && validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </Alert>
      )}

      {/* ミドルウェア項目の一覧 */}
      {middlewares.length > 0 ? (
        middlewares.map((middleware, index) => {
          const middlewareNameError = getFieldError(index, 'middleware_name');
          const productNameError = getFieldError(index, 'middleware_product_name');

          return (
            <Box
              key={index}
              ref={el => formRefs.current[index] = el}
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                p: 3,
                mb: 3,
                position: 'relative'
              }}
              data-error={!!(middlewareNameError || productNameError)}
            >
              {/* 削除ボタン */}
              <IconButton
                onClick={() => handleDeleteConfirm(index)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: '#dc2626',
                  '&:hover': {
                    backgroundColor: '#fef2f2',
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>

              <Grid container spacing={3}>
                {/* カテゴリー */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography sx={{ mb: 1 }}>カテゴリー</Typography>
                    <Autocomplete
                      freeSolo
                      options={getFilteredMiddlewareOptions(index)}
                      getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
                      value={middleware.middleware_name || ''}
                      onChange={(event, newValue) => handleMiddlewareChange(index, newValue)}
                      onInputChange={(event, newInputValue) => {
                        // 手動入力時の値を即座に反映
                        if (event && event.type === 'change') {
                          handleMiddlewareChange(index, newInputValue);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="カテゴリーを選択または入力してください"
                          variant="outlined"
                          error={!!middlewareNameError}
                          helperText={middlewareNameError?.message}
                          onBlur={(event) => {
                            // フォーカスアウト時に手動入力値を確定
                            const inputValue = event.target.value;
                            if (inputValue && inputValue !== middleware.middleware_name) {
                              handleMiddlewareChange(index, inputValue);
                            }
                          }}
                          sx={{
                            '& input:focus': {
                              boxShadow: 'none',
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                </Grid>

                {/* サービス */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography sx={{ mb: 1 }}>サービス（複数選択可）</Typography>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={getFilteredProductOptions(index)}
                      getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
                      value={middleware.middleware_product_name ? middleware.middleware_product_name.split(', ').filter(f => f.trim() !== '') : []}
                      inputValue={productInputValues[index] || ''}
                      onChange={(event, newValue) => handleProductChange(index, newValue)}
                      onInputChange={(event, newInputValue) => handleProductInputChange(index, newInputValue)}
                      renderTags={(value, getTagProps) =>
                        value.map((option, tagIndex) => (
                          <Chip
                            variant="filled"
                            label={option}
                            {...getTagProps({ index: tagIndex })}
                            key={tagIndex}
                            sx={{
                              backgroundColor: '#4F46E5',
                              color: 'white',
                              '& .MuiChip-deleteIcon': {
                                color: 'white',
                                '&:hover': {
                                  color: '#f0f0f0'
                                }
                              }
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="サービスを選択または入力してください"
                          variant="outlined"
                          error={!!productNameError}
                          helperText={productNameError?.message}
                          onBlur={() => handleProductBlur(index)}
                          sx={{
                            '& input:focus': {
                              boxShadow: 'none',
                            },
                          }}
                        />
                      )}
                      sx={{
                        '& .MuiAutocomplete-tag': {
                          margin: '2px',
                        },
                        '& .MuiAutocomplete-inputRoot': {
                          minHeight: '56px',
                          padding: '8px 12px',
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          );
        })
      ) : (
        // すべてのフォームが削除された場合のメッセージ
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            ミドルウェアの項目を追加してください
          </Typography>
        </Box>
      )}

      {/* 追加ボタン */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Fab
          color="primary"
          onClick={handleAdd}
          sx={{
            bgcolor: '#4F46E5',
            '&:hover': {
              bgcolor: '#4338CA',
            }
          }}
        >
          <AddIcon />
        </Fab>
      </Box>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, index: null })}
      >
        <DialogTitle>削除の確認</DialogTitle>
        <DialogContent>
          <Typography>
            本当に削除しますか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmDialog({ open: false, index: null })}
            color="primary"
          >
            キャンセル
          </Button>
          <Button
            onClick={() => handleDelete(deleteConfirmDialog.index)}
            color="error"
            variant="contained"
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
